import { Fragment } from "react";
import { ArrowRight, ArrowDown, Copy, ImageIcon } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionHeading } from "./SectionHeading";
import copy from "@/content/homepage-copy.json";

// Mini-makiety w kartach (HTML/CSS — tekst nie skaluje się z obrazem).
const MockChips = () => {
  const chips = ["PRODUKT", "REKLAMA", "WNĘTRZE", "ROLKA", "WIZERUNEK"];
  return (
    <div className="flex flex-wrap gap-2 rounded-field border border-brand-line bg-brand-bg/60 p-3">
      {chips.map((c, i) => (
        <span
          key={c}
          className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
            i === 0 ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-sec"
          }`}
        >
          {c}
        </span>
      ))}
    </div>
  );
};

const MockPrompt = () => (
  <div className="relative rounded-field border border-brand-line bg-brand-bg/60 p-3">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-muted">Profesjonalne zdjęcie produktu</p>
    <p className="mt-1 text-[12px] text-brand-text">Stwórz realistyczne ujęcie premium…</p>
    <p className="mt-0.5 text-[11px] text-brand-muted">światło studyjne • obiektyw 85 mm</p>
    <span className="mt-2 inline-flex items-center gap-1 rounded-md bg-brand-accent px-2.5 py-1 text-[10px] font-bold text-brand-on">
      <Copy size={11} strokeWidth={2} aria-hidden="true" /> KOPIUJ
    </span>
  </div>
);

const MockResult = () => (
  <div className="flex items-center gap-3 rounded-field border border-brand-accent/40 bg-brand-bg/60 p-3">
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-brand-accent/50 text-brand-accent">
      <ImageIcon size={22} strokeWidth={1.75} aria-hidden="true" />
    </span>
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-brand-text">Gotowy materiał</p>
      <p className="text-[11px] text-brand-muted">do publikacji i sprzedaży</p>
    </div>
  </div>
);

const MOCKS = [MockChips, MockPrompt, MockResult];

const StepCard = ({ step, index }) => {
  const Mock = MOCKS[index] || MockChips;
  return (
    <div
      className="hover-glow flex h-full flex-1 flex-col rounded-card border border-brand-line bg-brand-surface p-7"
      data-testid={`how-step-${index}`}
    >
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-accent font-display text-sm font-bold text-brand-on">
          {step.number}
        </span>
        <span className="font-display text-xs font-bold uppercase tracking-[0.1em] text-brand-accent">{step.label}</span>
      </div>
      <Mock />
      <h3 className="mt-6 font-display text-[22px] font-semibold leading-snug text-brand-text">{step.title}</h3>
      <p className="mt-2 text-[15px] leading-relaxed text-brand-sec">{step.text}</p>
    </div>
  );
};

const Connector = () => (
  <div className="flex shrink-0 items-center justify-center text-brand-accent" aria-hidden="true">
    <ArrowRight size={22} strokeWidth={2} className="hidden lg:block" />
    <ArrowDown size={22} strokeWidth={2} className="lg:hidden" />
  </div>
);

export const HowItWorks = () => {
  const h = copy.howItWorks;
  return (
    <section id="jak-to-dziala" className="mx-auto max-w-content px-4 py-14 md:px-8 md:py-[88px]" data-testid="how-section">
      <SectionHeading chapter={h.eyebrow} title={h.title} lead={h.lead} size="lg" />

      <Reveal delay={0.1}>
        <div className="mt-14 flex flex-col items-stretch gap-4 lg:flex-row lg:items-stretch">
          {h.steps.map((step, i) => (
            <Fragment key={step.number}>
              <StepCard step={step} index={i} />
              {i < h.steps.length - 1 && <Connector />}
            </Fragment>
          ))}
        </div>
      </Reveal>

      {/* pasek podsumowania + CTA */}
      <Reveal delay={0.15}>
        <div
          className="glow-strong mt-6 flex flex-col items-stretch gap-4 rounded-panel border border-brand-accent/30 bg-brand-elevated p-6 sm:flex-row sm:items-center sm:justify-between md:p-8"
          data-testid="how-summary"
        >
          <div>
            <p className="font-display text-lg font-bold text-brand-text sm:text-xl">{h.summary}</p>
            <p className="mt-1 text-[15px] text-brand-sec">{h.summarySub}</p>
          </div>
        </div>
      </Reveal>
    </section>
  );
};
